import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    ArrowLeft,
    Clock,
    Shield,
    CheckCircle2,
    XCircle,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Award,
} from 'lucide-react';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import * as ResultModule from 'modules/result';
import { RenderedHtml } from 'modules/editor';
import { AnswerReviewBody, hasAnswer } from 'components/result/AnswerReviewBody';
import { ReportQuestionButton } from 'modules/questionReports';
import { cn } from 'lib/utils';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'modules/auth/context';
import { estimateCategoryScore } from 'modules/test/category-scoring';
import { ScaledReportCard } from 'modules/standardizedExam/components/report/ScaledReportCard';
import { ShareScoreButton } from 'modules/standardizedExam/components/share/ShareScoreButton';
import type { SpeakingClip } from 'modules/standardizedExam/components/report/SpeakingClips';
import { SessionReview } from 'pages/Portal/SessionReview';
import { ExamInsightsProvider, InsightsUnlockBanner, useExamInsightsUnlock } from 'modules/examInsights';
import { useSessionReview } from 'modules/studyPlan';
import { FeedbackCard } from 'modules/feedback';
import { WeakTopics } from 'components/result/WeakTopics';
import { shouldShowWeakTopics, tallyItems, topicBreakdown, weakTopics } from 'components/result/topicBreakdown';

const SECTION_COLORS = ['#560591', '#0EA5E9', '#16A34A', '#EA580C', '#DC2626', '#7C3AED'];
const colorForSection = (order: number) => SECTION_COLORS[order % SECTION_COLORS.length];

const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins < 60) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ${mins % 60}m`;
};

const formatDate = (d: Date) =>
    new Date(d).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

type AnswerItem = {
    questionId: string;
    questionText: string;
    questionType: string;
    sectionId: string;
    sectionTitle: string;
    answer: string | string[];
    correctAnswer?: string | string[];
    isCorrect: boolean;
    pointsEarned: number;
    maxPoints: number;
    timeSpent: number;
    options:
        | Array<{ id: string; value: string; isCorrect?: boolean; isSelected: boolean }>
        | null;
    matchPairs: Array<{ key: string; correctValue?: string; selectedValue: string }> | null;
    answerRaw?: unknown;
    correctAnswerRaw?: unknown;
    questionData?: Record<string, any> | null;
    explanation?: string;
    aiReasoning?: string;
    aiAnalysis?: { available: boolean; texts: Record<string, string> };
    aiSubAnalysis?: Record<string, { available: boolean; texts: Record<string, string>; locked?: boolean }>;
    /** Items right out of items asked: a passage counts each sub-question, so Reading reads 28/40, not 0/1. */
    itemsCorrect: number;
    itemsTotal: number;
    gradingPending?: boolean;
    speakingRecordings?: SpeakingClip[];
};

type SectionGroup = {
    id: string;
    title: string;
    color: string;
    answers: AnswerItem[];
    start: number;
    end: number;
};

const TestResults: React.FC = () => {
    const { t } = useTranslation();
    const { state: authState } = useAuthContext();
    const { id } = useParams<{ id: string }>();
    const { pathname } = useLocation();
    // Reused by both shells: staff at /app/results/:id (back to History) and
    // candidates at /portal/results/:id (back to their portal Results list).
    const isPortal = pathname.startsWith('/portal');
    const backTo = isPortal ? '/portal/results' : '/app/history';
    const backLabel = isPortal ? t('portal.back_to_results') : t('back_to_history');
    const [currentIndex, setCurrentIndex] = useState(0);
    const [openSectionIds, setOpenSectionIds] = useState<Set<string>>(new Set());
    const { item, isLoading, isError, error, refetch, isFetching } = ResultModule.Hooks.useSingle({
        id: id ?? '',
        enabled: !!id,
    });
    // The candidate's own study review already lists their weak topics when it has any; this is the same query it makes.
    const { data: studyReview, isLoading: studyReviewLoading } = useSessionReview(id, isPortal);
    // IELTS/SAT on the marketplace: the score is free, the full analysis unlocks with a plan credit.
    const insightsUnlock = useExamInsightsUnlock(id, item?.insights?.locked === true, () => void refetch());

    // Group answers by sectionId, preserving the order they arrive in.
    const groups = useMemo<SectionGroup[]>(() => {
        if (!item?.answers?.length) return [];
        const order: string[] = [];
        const byId = new Map<string, SectionGroup>();
        let cursor = 0;
        item.answers.forEach((a) => {
            const ans = a as AnswerItem;
            const sid = ans.sectionId || '__unsectioned__';
            if (!byId.has(sid)) {
                order.push(sid);
                byId.set(sid, {
                    id: sid,
                    title: ans.sectionTitle || t('questions'),
                    color: colorForSection(order.length - 1),
                    answers: [],
                    start: 0,
                    end: 0,
                });
            }
            byId.get(sid)!.answers.push(ans);
        });
        return order.map((sid) => {
            const g = byId.get(sid)!;
            const start = cursor;
            cursor += g.answers.length;
            return { ...g, start, end: cursor };
        });
    }, [item, t]);

    const weak = useMemo(() => weakTopics(topicBreakdown((item?.answers ?? []) as AnswerItem[])), [item]);

    const currentGroup = useMemo(
        () => groups.find((g) => currentIndex >= g.start && currentIndex < g.end),
        [groups, currentIndex],
    );

    useEffect(() => {
        if (currentGroup) {
            setOpenSectionIds((prev) => {
                if (prev.has(currentGroup.id)) return prev;
                const next = new Set(prev);
                next.add(currentGroup.id);
                return next;
            });
        }
    }, [currentGroup]);

    const categoryEstimate = useMemo(() => {
        if (!item) return null;
        const testObj = item.test;
        return estimateCategoryScore({
            category: testObj?.category,
            percentage: item.percentage ?? 0,
            title: testObj?.title,
            tags: testObj?.tags,
            subjectConfigsCount: testObj?.subjectConfigs?.length,
            scaledReport: item.scaledReport,
            userGoalLevel: authState?.user?.learnerProfile?.goals?.find((g) => {
                const cat = (testObj?.category || '').toLowerCase();
                if (cat.includes('milliy') && g.exam === 'MILLIY_SERTIFIKAT') return true;
                if (cat === 'dtm' && g.exam === 'DTM') return true;
                if (cat === 'ielts' && g.exam === 'IELTS') return true;
                if (cat === 'sat' && g.exam === 'SAT') return true;
                if ((cat === 'attestation' || cat === 'attestatsiya') && g.exam === 'ATTESTATSIYA') return true;
                return false;
            })?.level,
        });
    }, [item, authState?.user?.learnerProfile?.goals]);

    if (!id) {
        return (
            <div className="ts-shell p-6">
                <div className="space-y-6">
                    <p className="text-muted-foreground">{t('no_result_id_provided')}</p>
                    <Link to={backTo}>
                        <Button variant="outline">{backLabel}</Button>
                    </Link>
                </div>
                <ReviewStyles />
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="ts-shell min-h-screen p-6">
                <div className="ts-page space-y-4">
                    <Skeleton className="h-20 w-full rounded-lg" />
                    <div className="ts-main-grid">
                        <Skeleton className="h-96 w-full rounded-lg" />
                        <Skeleton className="h-96 w-full rounded-lg" />
                    </div>
                </div>
                <ReviewStyles />
            </div>
        );
    }

    if (isError || !item) {
        return (
            <div className="ts-shell min-h-screen p-6">
                <div className="ts-page space-y-6">
                    <p className="text-destructive font-medium">
                        {error instanceof Error ? error.message : t('failed_to_load_history')}
                    </p>
                    <Link to={backTo}>
                        <Button variant="outline">{backLabel}</Button>
                    </Link>
                </div>
                <ReviewStyles />
            </div>
        );
    }

    const { test, percentage, passed, startedAt, duration } = item;
    const flatAnswers = (item.answers as AnswerItem[]) || [];
    const totalQuestions = flatAnswers.length;
    const current = flatAnswers[currentIndex];
    // Scores count items, not question documents: a 40-question Reading passage is 40, not 1.
    const { correct: correctCount, total: totalItems } = tallyItems(flatAnswers);

    const showWeakTopics = shouldShowWeakTopics({
        hasScaledReport: Boolean(item.scaledReport),
        isPortal,
        studyReviewLoading,
        studyReviewJudged: studyReview?.facts.judged ?? 0,
    });

    const goToQuestion = (idx: number) => {
        if (idx >= 0 && idx < flatAnswers.length) setCurrentIndex(idx);
    };

    const toggleSection = (sectionId: string) => {
        setOpenSectionIds((prev) => {
            const next = new Set(prev);
            if (next.has(sectionId)) next.delete(sectionId);
            else next.add(sectionId);
            return next;
        });
    };

    return (
        <ExamInsightsProvider value={insightsUnlock.state}>
        <div className="ts ts-shell min-h-screen">
            <div className="ts-page">
                {/* Top bar card */}
                <div className="ts-card">
                    <div className="ts-topbar">
                        <div className="ts-title-row">
                            <Link to={backTo} className="ts-back-btn" aria-label={backLabel}>
                                <ArrowLeft size={16} />
                            </Link>
                            <div className="ts-logo">
                                <Shield size={16} />
                            </div>
                            <div className="ts-title-text">
                                <div className="ts-title">{test?.title || t('assessment_complete')}</div>
                                <div className="ts-meta">
                                    {totalQuestions > 0 && (
                                        <>
                                            {t('question')} {currentIndex + 1} {t('of')} {totalQuestions}
                                            {' · '}
                                        </>
                                    )}
                                    {startedAt ? formatDate(startedAt) : ''}
                                </div>
                            </div>
                        </div>
                        <div className="ts-topbar-right">
                            {categoryEstimate?.scoreDisplay && (
                                <span
                                    className={cn('ts-category-chip', `ts-category-chip-${categoryEstimate.colorTheme}`)}
                                    title={
                                        categoryEstimate.goalComparison
                                            ? `${categoryEstimate.feedbackMessage} — ${categoryEstimate.goalComparison.message}`
                                            : categoryEstimate.feedbackMessage
                                    }
                                >
                                    <Award size={14} />
                                    {categoryEstimate.scoreDisplay}
                                </span>
                            )}
                            <span
                                className={cn(
                                    'ts-score-chip',
                                    passed == null
                                        ? 'ts-score-chip-neutral'
                                        : passed
                                        ? 'ts-score-chip-pass'
                                        : 'ts-score-chip-fail',
                                )}
                            >
                                {passed != null && (passed ? <CheckCircle2 size={14} /> : <XCircle size={14} />)}
                                {percentage}%
                            </span>
                            {passed != null && (
                                <span className="ts-meta-chip">
                                    <Award size={14} />
                                    {passed ? t('passed') : t('not_passed')}
                                </span>
                            )}
                            {categoryEstimate?.goalComparison && (
                                <span
                                    className={cn(
                                        'ts-meta-chip',
                                        categoryEstimate.goalComparison.isMet
                                            ? 'ts-goal-chip-met'
                                            : 'ts-goal-chip-missed',
                                    )}
                                    title={categoryEstimate.goalComparison.message}
                                >
                                    {categoryEstimate.goalComparison.isMet
                                        ? '🎯 ' + t('goal_met', 'Maqsadga erishildi')
                                        : '🎯 ' + t('target', 'Maqsad') + ': ' + categoryEstimate.goalComparison.userTarget}
                                </span>
                            )}
                            <span className="ts-meta-chip ts-meta-chip-muted">
                                <Clock size={14} />
                                {formatDuration(duration)}
                            </span>
                        </div>
                    </div>
                    <div className="ts-progress-row">
                        <span className="ts-progress-label">{t('score_2')}</span>
                        <div className="ts-progress-bar">
                            <div
                                className={cn(
                                    'ts-progress-fill',
                                    passed == null
                                        ? 'ts-progress-fill-neutral'
                                        : passed
                                        ? 'ts-progress-fill-pass'
                                        : 'ts-progress-fill-fail',
                                )}
                                style={{ width: `${percentage}%` }}
                            />
                        </div>
                        <span
                            className="ts-progress-label"
                            style={{ color: 'var(--ts-gray-900)', fontWeight: 600 }}
                        >
                            {correctCount}/{totalItems} {t('correct')}
                        </span>
                    </div>
                </div>

                <InsightsUnlockBanner
                    analysesRemaining={insightsUnlock.analysesRemaining}
                    analysisLimit={insightsUnlock.analysisLimit}
                    aiGradingPending={item.insights?.aiGradingPending === true}
                />

                {item.scaledReport && (
                    <ScaledReportCard report={item.scaledReport} className="my-4" onRefresh={() => refetch()} refreshing={isFetching} />
                )}
                {/* Learners share their own band or score; staff reading a result do not get the button. */}
                {isPortal && item.scaledReport && (
                    <ShareScoreButton report={item.scaledReport} testTitle={test?.title || ''} className="mb-4" />
                )}

                {/* Candidates only: staff open the same page from /app/results and see no coaching. */}
                {isPortal && <SessionReview key={id} attemptId={id} />}

                {showWeakTopics && <WeakTopics topics={weak} className="my-4" />}

                <div className={cn("ts-main-grid", current?.questionType === 'PASSAGE' && "ts-reading-grid")}>
                    <motion.div
                        key={currentIndex}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.18 }}
                        className={cn("ts-card ts-q-card", current?.questionType === 'PASSAGE' && "ts-passage-card col-span-full max-w-full")}
                    >
                        {current ? (
                            <>
                                <div className="ts-q-header">
                                    <div>
                                        <div className="ts-q-num">
                                            {t('question')} {currentIndex + 1}
                                        </div>
                                        <div className="ts-q-tags">
                                            <span className="ts-q-type">
                                                {t(
                                                    current.questionType === 'MULTIPLE_CHOICE'
                                                        ? 'single_choice'
                                                        : current.questionType === 'MULTIPLE_RESPONSE'
                                                          ? 'multiple_response'
                                                          : current.questionType === 'TRUE_FALSE'
                                                            ? 'true_false'
                                                            : current.questionType === 'SHORT_ANSWER'
                                                              ? 'short_answer'
                                                              : current.questionType?.toLowerCase() ??
                                                                'question',
                                                )}
                                            </span>
                                            <span className="ts-q-pts">
                                                · {current.pointsEarned}/{current.maxPoints}{' '}
                                                {t(current.maxPoints > 1 ? 'pts' : 'pt')}
                                            </span>
                                        </div>
                                    </div>
                                    <span
                                        className={cn(
                                            'ts-result-badge',
                                            !hasAnswer(current.answer)
                                                ? 'ts-result-badge-skipped'
                                                : current.isCorrect
                                                  ? 'ts-result-badge-correct'
                                                  : 'ts-result-badge-incorrect',
                                        )}
                                    >
                                        {!hasAnswer(current.answer) ? (
                                            <>
                                                <XCircle size={12} />
                                                {t('not_answered')}
                                            </>
                                        ) : current.isCorrect ? (
                                            <>
                                                <CheckCircle2 size={12} />
                                                {t('correct')}
                                            </>
                                        ) : (
                                            <>
                                                <XCircle size={12} />
                                                {t('incorrect')}
                                            </>
                                        )}
                                    </span>
                                </div>

                                {current.questionType !== 'PASSAGE' ? (
                                    <div className="ts-q-prompt">
                                        {current.questionText ? (
                                            <RenderedHtml html={current.questionText} />
                                        ) : (
                                            t('question')
                                        )}
                                    </div>
                                ) : (
                                    Boolean(
                                        current.questionText &&
                                        current.questionText !== current.questionData?.passageText &&
                                        current.questionText !== current.questionData?.pages?.[0]?.passageText
                                    ) && (
                                        <div className="ts-q-prompt mb-4">
                                            <RenderedHtml html={current.questionText} />
                                        </div>
                                    )
                                )}

                                <AnswerReviewBody answer={current} correctAnswersHidden={item.correctAnswersHidden} attemptId={id} />

                                {/* The learner's own review only: the API refuses (and the button hides) for anyone else's attempt. */}
                                {id && current.questionId && (
                                    <div className="mt-4 flex justify-end">
                                        <ReportQuestionButton attemptId={id} questionId={current.questionId} withLabel />
                                    </div>
                                )}

                                <div className="ts-q-footer">
                                    <button
                                        type="button"
                                        className="ts-btn ts-btn-secondary"
                                        onClick={() => goToQuestion(currentIndex - 1)}
                                        disabled={currentIndex === 0}
                                    >
                                        <ChevronLeft size={14} />
                                        {t('exam.nav.previous')}
                                    </button>
                                    <span className="ts-autosave">
                                        {currentIndex + 1} / {totalQuestions}
                                    </span>
                                    <button
                                        type="button"
                                        className="ts-btn ts-btn-primary"
                                        onClick={() => goToQuestion(currentIndex + 1)}
                                        disabled={currentIndex >= totalQuestions - 1}
                                    >
                                        {t('next')}
                                        <ChevronRight size={14} />
                                    </button>
                                </div>
                            </>
                        ) : (
                            <p className="text-muted-foreground">{t('no_results')}</p>
                        )}
                    </motion.div>

                    <aside className="ts-side">
                        {/* Renders nothing for anyone who is not the attempt's owner, such as staff reading a candidate's result. */}
                        {id && item && item.status !== 'in_progress' && <FeedbackCard attemptId={id} />}
                        <div className="ts-card ts-nav-card">
                            <div className="ts-nav-head">
                                <span className="ts-eyebrow">{t('question_navigator')}</span>
                                <span className="ts-nav-count">
                                    {correctCount}/{totalItems}
                                </span>
                            </div>
                            {groups.map(({ id: sid, title, color, answers, start }) => {
                                const { correct: sectionCorrect, total: sectionTotal } = tallyItems(answers);
                                const sectionPct = sectionTotal
                                    ? (sectionCorrect / sectionTotal) * 100
                                    : 0;
                                const open = openSectionIds.has(sid);
                                return (
                                    <div key={sid} className="ts-subj-block">
                                        <button
                                            type="button"
                                            className="ts-subj-toggle"
                                            aria-expanded={open}
                                            onClick={() => toggleSection(sid)}
                                        >
                                            <span className="ts-subj-left">
                                                <span
                                                    className="ts-subj-dot"
                                                    style={{ background: color }}
                                                />
                                                <span className="ts-subj-name">{title}</span>
                                            </span>
                                            <span className="ts-subj-right">
                                                <span className="ts-subj-count">
                                                    {sectionCorrect}/{sectionTotal}
                                                </span>
                                                <span
                                                    className="ts-subj-chevron"
                                                    style={{
                                                        transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                                                    }}
                                                >
                                                    <ChevronDown size={14} />
                                                </span>
                                            </span>
                                        </button>
                                        <div className="ts-subj-progress">
                                            <div style={{ width: `${sectionPct}%`, background: color }} />
                                        </div>
                                        <div className="ts-subj-grid-wrap" data-open={open}>
                                            <div className="ts-nav-grid">
                                                {answers.map((a, i) => {
                                                    const globalIdx = start + i;
                                                    const isCurrent = globalIdx === currentIndex;
                                                    const answered = hasAnswer(a.answer);
                                                    const tileClass = isCurrent
                                                        ? 'ts-nav-tile-current'
                                                        : !answered
                                                          ? 'ts-nav-tile-skipped'
                                                          : a.isCorrect
                                                            ? 'ts-nav-tile-correct'
                                                            : 'ts-nav-tile-incorrect';
                                                    return (
                                                        <button
                                                            key={a.questionId}
                                                            type="button"
                                                            className={cn('ts-nav-tile', tileClass)}
                                                            onClick={() => goToQuestion(globalIdx)}
                                                            aria-label={`${t('question')} ${globalIdx + 1}`}
                                                        >
                                                            {globalIdx + 1}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            <div className="ts-legend-row">
                                <ReviewLegend t={t} />
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
            <ReviewStyles />
        </div>
        </ExamInsightsProvider>
    );
};

const ReviewLegend: React.FC<{ t: (k: string) => string }> = ({ t }) => (
    <div className="ts-legend">
        <span className="ts-legend-item">
            <span
                className="ts-legend-swatch"
                style={{ background: 'var(--ts-primary)', borderColor: 'var(--ts-primary)' }}
            />
            {t('current')}
        </span>
        <span className="ts-legend-item">
            <span className="ts-legend-swatch ts-legend-swatch-success" />
            {t('correct')}
        </span>
        <span className="ts-legend-item">
            <span className="ts-legend-swatch ts-legend-swatch-danger" />
            {t('incorrect')}
        </span>
        <span className="ts-legend-item">
            <span className="ts-legend-swatch ts-legend-swatch-empty" />
            {t('not_answered')}
        </span>
    </div>
);

const ReviewStyles: React.FC = () => (
    <style>{`
.ts-shell {
    --ts-primary: #171717;
    --ts-primary-fg: #FFFFFF;
    --ts-primary-50: #F4F4F5;
    --ts-primary-200: #D4D4D8;
    --ts-primary-600: #000000;
    --ts-ring: rgba(23, 23, 23, 0.16);
    --ts-bg: #FFFFFF;
    --ts-card-bg: #FFFFFF;
    --ts-gray-100: #F3F4F6;
    --ts-gray-200: #E5E7EB;
    --ts-gray-300: #D1D5DB;
    --ts-gray-400: #9CA3AF;
    --ts-gray-500: #6B7280;
    --ts-gray-600: #4B5563;
    --ts-gray-700: #374151;
    --ts-gray-900: #111827;
    --ts-success-bg: #F0FDF4;
    --ts-success-border: #BBF7D0;
    --ts-success-text: #15803D;
    --ts-success-strong: #16A34A;
    --ts-danger-bg: #FEF2F2;
    --ts-danger-border: #FECACA;
    --ts-danger-text: #B91C1C;
    --ts-danger-strong: #DC2626;
    background: var(--ts-bg);
    font-family: 'Inter', system-ui, sans-serif;
    color: var(--ts-gray-900);
}
.dark .ts-shell {
    --ts-primary: #FAFAFA;
    --ts-primary-fg: #0A0A0A;
    --ts-primary-50: rgba(250, 250, 250, 0.10);
    --ts-primary-200: rgba(250, 250, 250, 0.32);
    --ts-primary-600: #E4E4E7;
    --ts-ring: rgba(250, 250, 250, 0.22);
    --ts-bg: hsl(var(--background));
    --ts-card-bg: hsl(var(--card));
    --ts-gray-100: hsl(var(--muted));
    --ts-gray-200: hsl(var(--border));
    --ts-gray-300: hsl(220 13% 35%);
    --ts-gray-400: hsl(220 9% 55%);
    --ts-gray-500: hsl(var(--muted-foreground));
    --ts-gray-600: hsl(220 9% 70%);
    --ts-gray-700: hsl(220 9% 80%);
    --ts-gray-900: hsl(var(--foreground));
    --ts-success-bg: rgba(34, 197, 94, 0.14);
    --ts-success-border: rgba(34, 197, 94, 0.4);
    --ts-success-text: #4ADE80;
    --ts-success-strong: #22C55E;
    --ts-danger-bg: rgba(239, 68, 68, 0.14);
    --ts-danger-border: rgba(239, 68, 68, 0.4);
    --ts-danger-text: #FCA5A5;
    --ts-danger-strong: #EF4444;
}
.ts h1, .ts h2, .ts h3, .ts h4 { margin: 0; }
.ts-page {
    max-width: 1280px; margin: 0 auto; padding: 28px;
    display: flex; flex-direction: column; gap: 18px;
}
.ts-card {
    background: var(--ts-card-bg);
    border: 1px solid var(--ts-gray-200);
    border-radius: 12px;
}
.ts-eyebrow {
    font-size: 11px; font-weight: 700;
    letter-spacing: 0.08em; text-transform: uppercase;
    color: var(--ts-gray-500);
}
.ts-topbar {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 18px; gap: 16px; flex-wrap: wrap;
}
.ts-title-row { display: flex; align-items: center; gap: 12px; min-width: 0; }
.ts-title-text { min-width: 0; }
.ts-title { font-size: 16px; font-weight: 700; letter-spacing: -0.02em; }
.ts-meta { font-size: 12px; color: var(--ts-gray-500); margin-top: 2px; }
.ts-topbar-right { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.ts-back-btn {
    width: 32px; height: 32px;
    display: inline-flex; align-items: center; justify-content: center;
    border-radius: 8px;
    color: var(--ts-gray-700);
    transition: background .15s ease;
    flex-shrink: 0;
}
.ts-back-btn:hover { background: var(--ts-gray-100); }
.ts-logo {
    width: 32px; height: 32px; border-radius: 8px;
    background: var(--ts-primary); color: var(--ts-primary-fg);
    display: inline-flex; align-items: center; justify-content: center;
    flex-shrink: 0;
}
.ts-score-chip {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 12px; border-radius: 999px;
    font-size: 13px; font-weight: 700;
    font-variant-numeric: tabular-nums;
}
.ts-score-chip-pass {
    background: var(--ts-success-bg); color: var(--ts-success-text); border: 1px solid var(--ts-success-border);
}
.ts-score-chip-fail {
    background: var(--ts-danger-bg); color: var(--ts-danger-text); border: 1px solid var(--ts-danger-border);
}
.ts-score-chip-neutral {
    background: var(--ts-primary-50); color: var(--ts-primary); border: 1px solid var(--ts-primary-200);
}
.ts-category-chip {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 12px; border-radius: 999px;
    font-size: 13px; font-weight: 700;
}
.ts-category-chip-gold {
    background: #fef3c7; color: #b45309; border: 1px solid #fde68a;
}
.ts-category-chip-emerald {
    background: #d1fae5; color: #047857; border: 1px solid #a7f3d0;
}
.ts-category-chip-blue {
    background: #dbeafe; color: #1d4ed8; border: 1px solid #bfdbfe;
}
.ts-category-chip-purple {
    background: #f3e8ff; color: #6d28d9; border: 1px solid #e9d5ff;
}
.ts-category-chip-amber {
    background: #fef3c7; color: #b45309; border: 1px solid #fde68a;
}
.ts-category-chip-rose {
    background: #ffe4e6; color: #be123c; border: 1px solid #fecdd3;
}
.ts-category-chip-slate {
    background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0;
}
.dark .ts-category-chip-gold {
    background: rgba(245, 158, 11, 0.15); color: #fbbf24; border-color: rgba(245, 158, 11, 0.4);
}
.dark .ts-category-chip-emerald {
    background: rgba(16, 185, 129, 0.15); color: #34d399; border-color: rgba(16, 185, 129, 0.4);
}
.dark .ts-category-chip-blue {
    background: rgba(59, 130, 246, 0.15); color: #60a5fa; border-color: rgba(59, 130, 246, 0.4);
}
.dark .ts-category-chip-purple {
    background: rgba(139, 92, 246, 0.15); color: #c084fc; border-color: rgba(139, 92, 246, 0.4);
}
.dark .ts-category-chip-amber {
    background: rgba(245, 158, 11, 0.15); color: #fbbf24; border-color: rgba(245, 158, 11, 0.4);
}
.dark .ts-category-chip-rose {
    background: rgba(244, 63, 94, 0.15); color: #fb7185; border-color: rgba(244, 63, 94, 0.4);
}
.dark .ts-category-chip-slate {
    background: rgba(148, 163, 184, 0.15); color: #cbd5e1; border-color: rgba(148, 163, 184, 0.4);
}
.ts-goal-chip-met {
    background: #d1fae5 !important; color: #047857 !important; border: 1px solid #a7f3d0 !important;
}
.dark .ts-goal-chip-met {
    background: rgba(16, 185, 129, 0.15) !important; color: #34d399 !important; border-color: rgba(16, 185, 129, 0.4) !important;
}
.ts-goal-chip-missed {
    background: #fef3c7 !important; color: #b45309 !important; border: 1px solid #fde68a !important;
}
.dark .ts-goal-chip-missed {
    background: rgba(245, 158, 11, 0.15) !important; color: #fbbf24 !important; border-color: rgba(245, 158, 11, 0.4) !important;
}
.ts-meta-chip {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 10px; border-radius: 999px;
    background: var(--ts-primary-50); color: var(--ts-primary);
    border: 1px solid var(--ts-primary-200);
    font-size: 12px; font-weight: 600;
}
.ts-meta-chip-muted {
    background: var(--ts-gray-100); color: var(--ts-gray-600);
    border-color: var(--ts-gray-200);
}
.ts-progress-row {
    display: flex; align-items: center; gap: 14px;
    padding: 12px 18px;
    border-top: 1px solid var(--ts-gray-100);
}
.ts-progress-bar {
    flex: 1; height: 6px;
    background: var(--ts-gray-100);
    border-radius: 999px; overflow: hidden;
}
.ts-progress-bar > div { height: 100%; border-radius: 999px; transition: width .3s ease; }
.ts-progress-fill-pass {
    background: var(--ts-success-strong);
}
.ts-progress-fill-fail {
    background: var(--ts-danger-strong);
}
.ts-progress-fill-neutral {
    background: var(--ts-primary);
}
.ts-progress-label {
    font-size: 12px; font-weight: 500; color: var(--ts-gray-600);
    font-variant-numeric: tabular-nums;
}

.ts-main-grid {
    display: grid; grid-template-columns: minmax(0, 1fr) 320px;
    gap: 18px; align-items: start;
}
.ts-reading-grid { grid-template-columns: minmax(0, 1fr); }
.ts-reading-grid > aside { display: none; }
.ts-passage-card { width: 100%; max-width: 100%; }
@media (max-width: 1023px) {
    .ts-main-grid { grid-template-columns: minmax(0, 1fr); }
    .ts-side { position: static !important; }
}
@media (max-width: 640px) {
    .ts-page { padding: 16px; gap: 14px; }
    .ts-topbar { padding: 12px 14px; }
    .ts-progress-row { padding: 10px 14px; }
    .ts-q-card { padding: 20px 18px; }
    .ts-q-prompt { font-size: 17px; }
    .ts-q-header { margin-bottom: 16px; }
    .ts-q-footer { margin-top: 20px; padding-top: 16px; gap: 8px; }
    .ts-btn { padding: 8px 12px; font-size: 12px; }
    .ts-btn-secondary { padding: 7px 10px; }
    .ts-autosave { display: none; }
}

.ts-q-card { padding: 28px 32px; }
.ts-q-header {
    display: flex; justify-content: space-between; align-items: flex-start;
    gap: 16px; margin-bottom: 20px;
}
.ts-q-tags { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.ts-q-num {
    font-size: 13px; font-weight: 700;
    color: var(--ts-gray-500); margin-bottom: 8px;
}
.ts-q-badge {
    font-size: 11px; font-weight: 700;
    padding: 4px 9px; border-radius: 999px;
    background: var(--ts-primary-50); color: var(--ts-primary);
    letter-spacing: 0.02em;
}
.ts-q-type {
    font-size: 11px; font-weight: 600;
    color: var(--ts-gray-500);
    text-transform: uppercase; letter-spacing: 0.06em;
}
.ts-q-pts { font-size: 12px; color: var(--ts-gray-500); font-weight: 500; }
.ts-q-prompt {
    font-size: 19px; font-weight: 600; line-height: 1.45;
    letter-spacing: -0.01em; color: var(--ts-gray-900);
    margin-bottom: 24px;
}
.ts-q-options { display: flex; flex-direction: column; gap: 8px; }

.ts-result-badge {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 10px; border-radius: 999px;
    font-size: 12px; font-weight: 600;
    flex-shrink: 0;
}
.ts-result-badge-correct {
    background: var(--ts-success-bg); color: var(--ts-success-text); border: 1px solid var(--ts-success-border);
}
.ts-result-badge-incorrect {
    background: var(--ts-danger-bg); color: var(--ts-danger-text); border: 1px solid var(--ts-danger-border);
}
.ts-result-badge-skipped {
    background: var(--ts-gray-100); color: var(--ts-gray-600); border: 1px solid var(--ts-gray-200);
}

.ts-review-opt {
    display: flex; align-items: flex-start; gap: 12px;
    padding: 14px 16px; border-radius: 10px;
    border: 1px solid var(--ts-gray-200); background: var(--ts-card-bg);
}
.ts-review-opt-correct {
    border-color: var(--ts-success-border); background: var(--ts-success-bg);
}
.ts-review-opt-incorrect {
    border-color: var(--ts-danger-border); background: var(--ts-danger-bg);
}
.ts-review-opt-mark {
    width: 20px; height: 20px; border-radius: 999px;
    border: 2px solid var(--ts-gray-300);
    flex-shrink: 0; margin-top: 1px;
    display: inline-flex; align-items: center; justify-content: center;
    background: var(--ts-card-bg);
}
.ts-review-opt-mark-square { border-radius: 6px; }
.ts-review-opt-mark-correct {
    border-color: var(--ts-success-strong); background: var(--ts-success-strong);
}
.ts-review-opt-mark-incorrect {
    border-color: var(--ts-danger-strong); background: var(--ts-danger-strong);
}
.ts-review-opt-mark-fill {
    width: 7px; height: 7px; background: #fff; border-radius: 999px;
}
.ts-opt-letter {
    font-size: 11px; font-weight: 700;
    color: var(--ts-gray-500); margin-right: 4px; margin-top: 2px;
    min-width: 12px;
}
.ts-review-opt-correct .ts-opt-letter { color: var(--ts-success-text); }
.ts-review-opt-incorrect .ts-opt-letter { color: var(--ts-danger-text); }
.ts-opt-text {
    font-size: 14px; line-height: 1.55; color: var(--ts-gray-700);
}
.ts-review-opt-correct .ts-opt-text { color: var(--ts-success-text); font-weight: 500; }
.ts-review-opt-incorrect .ts-opt-text { color: var(--ts-danger-text); font-weight: 500; }
.ts-review-opt-icon { margin-left: auto; flex-shrink: 0; }

.ts-review-fill {
    padding: 14px 16px; border-radius: 10px; border: 1px solid var(--ts-gray-200);
    background: var(--ts-card-bg);
}
.ts-review-fill-empty { background: var(--ts-gray-100); }
.ts-review-fill-correct { background: var(--ts-success-bg); border-color: var(--ts-success-border); }
.ts-review-fill-incorrect { background: var(--ts-danger-bg); border-color: var(--ts-danger-border); }
.ts-review-fill-label {
    font-size: 11px; font-weight: 600;
    color: var(--ts-gray-500);
    text-transform: uppercase; letter-spacing: 0.04em;
    margin-bottom: 4px;
}
.ts-review-fill-value { font-size: 14px; font-weight: 500; color: var(--ts-gray-900); }
.ts-review-fill-empty .ts-review-fill-value {
    color: var(--ts-gray-500); font-style: italic;
}
.ts-review-fill-correct .ts-review-fill-value { color: var(--ts-success-text); }
.ts-review-fill-incorrect .ts-review-fill-value { color: var(--ts-danger-text); }

.ts-review-match-row {
    display: grid; grid-template-columns: 1fr auto 1fr;
    gap: 8px; align-items: center;
}
.ts-review-match-key {
    padding: 10px 12px; border-radius: 8px;
    background: var(--ts-gray-100); color: var(--ts-gray-900);
    font-size: 14px; font-weight: 500;
}
.ts-review-match-eq { color: var(--ts-gray-400); font-size: 12px; }
.ts-review-match-val {
    padding: 10px 12px; border-radius: 8px;
    border: 1px solid var(--ts-gray-200); background: var(--ts-card-bg);
    font-size: 14px; font-weight: 500;
}
.ts-review-match-val-empty {
    background: var(--ts-gray-100); color: var(--ts-gray-500); font-style: italic;
}
.ts-review-match-val-correct {
    background: var(--ts-success-bg); border-color: var(--ts-success-border); color: var(--ts-success-text);
}
.ts-review-match-val-incorrect {
    background: var(--ts-danger-bg); border-color: var(--ts-danger-border); color: var(--ts-danger-text);
}
.ts-review-match-correction {
    display: block; font-size: 12px; font-weight: 400;
    color: var(--ts-success-text); margin-top: 2px;
}

.ts-q-footer {
    display: flex; justify-content: space-between; align-items: center;
    gap: 12px;
    margin-top: 28px; padding-top: 20px;
    border-top: 1px solid var(--ts-gray-100);
}
.ts-autosave {
    flex: 1; min-width: 0; text-align: center;
    font-size: 12px; color: var(--ts-gray-500);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    font-variant-numeric: tabular-nums;
}
.ts-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 9px 16px; border-radius: 8px;
    font-family: inherit; font-size: 13px; font-weight: 600;
    cursor: pointer; transition: background .15s ease;
    border: 1px solid transparent;
}
.ts-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.ts-btn-primary { background: var(--ts-primary); color: var(--ts-primary-fg); }
.ts-btn-primary:hover:not(:disabled) { background: var(--ts-primary-600); }
.ts-btn-secondary {
    background: var(--ts-card-bg); color: var(--ts-gray-900);
    border-color: var(--ts-gray-200); padding: 8px 14px;
}
.ts-btn-secondary:hover:not(:disabled) {
    background: var(--ts-gray-100); border-color: var(--ts-gray-300);
}

.ts-side {
    display: flex; flex-direction: column; gap: 14px;
    position: sticky; top: 18px;
}
.ts-nav-card { padding: 0; }
.ts-nav-head {
    padding: 14px 16px;
    border-bottom: 1px solid var(--ts-gray-100);
    display: flex; justify-content: space-between; align-items: center;
}
.ts-nav-count {
    font-size: 11px; color: var(--ts-gray-500);
    font-weight: 600; font-variant-numeric: tabular-nums;
}
.ts-subj-block { padding: 14px 16px; }
.ts-subj-block + .ts-subj-block { border-top: 1px solid var(--ts-gray-100); }
.ts-subj-toggle {
    display: flex; align-items: center; justify-content: space-between;
    width: 100%; background: transparent; border: 0;
    padding: 0; cursor: pointer; font-family: inherit; text-align: left;
    margin-bottom: 10px;
}
.ts-subj-left { display: flex; align-items: center; gap: 10px; min-width: 0; }
.ts-subj-right { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
.ts-subj-dot { width: 8px; height: 8px; border-radius: 999px; flex-shrink: 0; }
.ts-subj-name {
    font-size: 13px; font-weight: 700;
    color: var(--ts-gray-900); letter-spacing: -0.01em;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.ts-subj-count {
    font-size: 11px; color: var(--ts-gray-500);
    font-weight: 500; font-variant-numeric: tabular-nums;
}
.ts-subj-chevron {
    color: var(--ts-gray-400);
    transition: transform .2s ease, color .2s ease;
    display: inline-flex; align-items: center; justify-content: center;
}
.ts-subj-toggle:hover .ts-subj-chevron { color: var(--ts-gray-600); }
.ts-subj-progress {
    height: 4px; background: var(--ts-gray-100);
    border-radius: 999px; overflow: hidden;
    margin-bottom: 12px;
}
.ts-subj-progress > div { height: 100%; border-radius: 999px; transition: width .3s ease; }
.ts-subj-grid-wrap {
    overflow: hidden;
    transition: max-height .25s ease, opacity .2s ease, margin-top .2s ease;
}
.ts-subj-grid-wrap[data-open='false'] { max-height: 0; opacity: 0; margin-top: 0; }
.ts-subj-grid-wrap[data-open='true'] { max-height: 1200px; opacity: 1; margin-top: 0; }

.ts-nav-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(34px, 1fr));
    gap: 6px; padding: 4px;
}
.ts-nav-tile {
    width: 100%; aspect-ratio: 1 / 1;
    min-width: 0;
    border-radius: 8px;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 13px; font-weight: 600;
    cursor: pointer; position: relative;
    border: 1px solid var(--ts-gray-200); background: var(--ts-card-bg);
    color: var(--ts-gray-700);
    transition: all .12s ease;
    font-variant-numeric: tabular-nums;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
}
.ts-nav-tile:hover { border-color: var(--ts-gray-300); }
.ts-nav-tile-correct {
    background: var(--ts-success-bg); border-color: var(--ts-success-border); color: var(--ts-success-text);
}
.ts-nav-tile-incorrect {
    background: var(--ts-danger-bg); border-color: var(--ts-danger-border); color: var(--ts-danger-text);
}
.ts-nav-tile-skipped {
    background: var(--ts-card-bg); border-color: var(--ts-gray-200); color: var(--ts-gray-400);
}
.ts-nav-tile-current {
    background: var(--ts-primary); border-color: var(--ts-primary);
    color: var(--ts-primary-fg); box-shadow: 0 0 0 3px var(--ts-ring);
}
.ts-legend-row {
    padding: 12px 16px;
    border-top: 1px solid var(--ts-gray-100);
}
.ts-legend { display: flex; gap: 12px; flex-wrap: wrap; }
.ts-legend-item {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 11px; color: var(--ts-gray-500); font-weight: 500;
}
.ts-legend-swatch {
    width: 12px; height: 12px; border-radius: 4px;
    border: 1px solid var(--ts-gray-200);
}
.ts-legend-swatch-success {
    background: var(--ts-success-bg); border-color: var(--ts-success-border);
}
.ts-legend-swatch-danger {
    background: var(--ts-danger-bg); border-color: var(--ts-danger-border);
}
.ts-legend-swatch-empty { background: var(--ts-card-bg); }
`}</style>
);

export default TestResults;
